import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import PaymentsIcon from '@mui/icons-material/Payments';
import CloseIcon from '@mui/icons-material/Close';
import {
  useGetSupplierDuesPageMutation,
  useLazyGetOpenInvoicesQuery,
  useLazyGetPurchasePaymentsQuery,
  usePaySupplierMutation,
} from '../../api/reports/ReportApi';
import { OpenInvoice, PurchasePayment, SupplierDue } from '../../types/reports';
import SearchField from '../common/SearchField';
import { useSnackbar } from '../../utils/snackbar';
import { can } from '../../utils/auth';

const money = (value?: number | null) => Number(value || 0).toFixed(2);
/*
 * The local day as yyyy-MM-dd.
 *
 * toISOString() converts to UTC first, which in India is five and a half hours
 * behind: between midnight and 05:30 it hands back yesterday. The field's max
 * then stopped at yesterday and a payment entered this morning was refused as
 * being in the future. Dashboard.tsx builds its dates the same way.
 */
const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const headCell = {
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  px: 1.5,
  py: 1,
};

/**
 * What the shop owes its suppliers, and paying it off.
 *
 * <p>Largest balance first, because that is the order a shop pays in. Opening a
 * supplier lists only the invoices with something still on them - a settled invoice
 * is not a decision to make.
 */
const SupplierDues = () => {
  const { openSnackbar } = useSnackbar();
  const [searchValue, setSearchValue] = useState('');
  const [onlyDue, setOnlyDue] = useState(true);

  const [rows, setRows] = useState<SupplierDue[]>([]);
  const [rowCount, setRowCount] = useState(0);
  /** Totals for the whole search, sent with the page - not a sum of what is shown. */
  const [totals, setTotals] = useState({ due: 0, billed: 0, paid: 0, invoices: 0 });
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [getSupplierDuesPage] = useGetSupplierDuesPageMutation();

  const load = () => {
    if (timeout.current) clearTimeout(timeout.current);
    setLoading(true);
    timeout.current = setTimeout(async () => {
      try {
        const response = await getSupplierDuesPage({
          searchValue,
          onlyDue,
          page: paginationModel.page,
          size: paginationModel.pageSize,
        }).unwrap();
        setRows(response?.data || []);
        setRowCount(response?.totalCount || 0);
        setTotals({
          due: Number(response?.totalDue || 0),
          billed: Number(response?.totalBilled || 0),
          paid: Number(response?.totalPaid || 0),
          invoices: Number(response?.invoiceCount || 0),
        });
      } catch {
        setRows([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    }, 500);
  };

  useEffect(() => {
    load();
    return () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, onlyDue, paginationModel.page, paginationModel.pageSize]);

  /**
   * Any change of filter puts the list back on the first page.
   *
   * <p>Page 4 of the old results is not page 4 of the new ones - usually it is past
   * the end of them, and the shop is shown an empty grid for a search that matched.
   */
  useEffect(() => {
    setPaginationModel((model) => (model.page === 0 ? model : { ...model, page: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, onlyDue]);
  const [getOpenInvoices] = useLazyGetOpenInvoicesQuery();
  const [getPayments] = useLazyGetPurchasePaymentsQuery();
  const [paySupplier, { isLoading: paying }] = usePaySupplierMutation();

  const [selected, setSelected] = useState<SupplierDue | null>(null);
  const [invoices, setInvoices] = useState<OpenInvoice[]>([]);
  const [payTarget, setPayTarget] = useState<OpenInvoice | null>(null);
  const [history, setHistory] = useState<PurchasePayment[]>([]);

  const [amount, setAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState<PurchasePayment['paymentMode']>('CASH');
  const [paymentDate, setPaymentDate] = useState(today());
  const [referenceNo, setReferenceNo] = useState('');



  const columns: GridColDef[] = [
    { field: 'supplierName', headerName: 'Supplier', flex: 1.4, minWidth: 200 },
    { field: 'invoiceCount', headerName: 'Invoices', flex: 1, minWidth: 100, align: 'center', headerAlign: 'center' },
    {
      field: 'billedAmount',
      headerName: 'Billed',
      flex: 1,
      minWidth: 120,
      align: 'right',
      headerAlign: 'right',
      renderCell: (row) => money(row.value as number),
    },
    {
      field: 'paidAmount',
      headerName: 'Paid',
      flex: 1,
      minWidth: 120,
      align: 'right',
      headerAlign: 'right',
      renderCell: (row) => money(row.value as number),
    },
    {
      field: 'dueAmount',
      headerName: 'Due',
      flex: 1,
      minWidth: 120,
      align: 'right',
      headerAlign: 'right',
      renderCell: (row) => (
        <Typography variant="body2" fontWeight={700} color={Number(row.value) > 0 ? 'error.main' : 'success.main'}>
          {money(row.value as number)}
        </Typography>
      ),
    },
    {
      field: 'Action',
      headerName: 'Action',
      flex: 1,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (row) =>
        Number(row.row.dueAmount) > 0 ? (
          <Button size="small" startIcon={<PaymentsIcon />} onClick={() => openSupplier(row.row as SupplierDue)}>
            Invoices
          </Button>
        ) : null,
    },
  ];

  const openSupplier = async (supplier: SupplierDue) => {
    setSelected(supplier);
    try {
      setInvoices(await getOpenInvoices(supplier.supplierId).unwrap());
    } catch {
      setInvoices([]);
    }
  };

  const openPayment = async (invoice: OpenInvoice) => {
    setPayTarget(invoice);
    setAmount(String(invoice.dueAmount));
    setPaymentMode('CASH');
    setPaymentDate(today());
    setReferenceNo('');
    try {
      setHistory(await getPayments(invoice.id).unwrap());
    } catch {
      setHistory([]);
    }
  };

  const submitPayment = async () => {
    if (!payTarget) return;
    const value = Number(amount);
    if (!value || value <= 0) {
      openSnackbar('Enter an amount greater than zero', 'warning');
      return;
    }
    /*
     * Both ends of the date, checked here rather than left to the calendar.
     *
     * The min and max on the box only steer the picker: a date typed straight in, or
     * one left behind after the field is cleared, reaches this handler untouched. So
     * a payment dated next year, or dated nothing at all, was still posted.
     */
    if (!paymentDate) {
      openSnackbar('Choose the date the money was paid', 'warning');
      return;
    }
    if (payTarget.invoiceDate && paymentDate < payTarget.invoiceDate) {
      openSnackbar(`The payment date cannot be before the invoice date, ${payTarget.invoiceDate}`, 'warning');
      return;
    }
    if (paymentDate > today()) {
      openSnackbar('The payment date cannot be in the future', 'warning');
      return;
    }
    try {
      await paySupplier({
        purchaseId: payTarget.id,
        amount: value,
        paymentMode,
        paymentDate,
        referenceNo: referenceNo.trim(),
      }).unwrap();
      openSnackbar(`Paid ${money(value)} against ${payTarget.invoiceNo}`, 'success');

      /*
       * Take the payment off the row we just paid, here and now.
       *
       * The server list below is the authority, but it is fetched through a cached
       * query and can come back with the figure from before the payment - which is how
       * a part payment of 1,000 against 6,000 left the Due column still reading 6,000.
       * Applying it locally means the row is right the moment the box closes, and the
       * refresh then confirms it rather than being the only thing that can.
       */
      const paidId = payTarget.id;
      // What this invoice must read once the payment lands. Captured now, because
      // payTarget is cleared below and the server list is checked against this.
      const expectedDue = Number(payTarget.dueAmount ?? 0) - value;
      const invoiceTotal = Number(payTarget.totalAmount ?? 0);
      setInvoices((current) =>
        current
          .map((invoice) =>
            invoice.id === paidId
              ? {
                  ...invoice,
                  paidAmount: Number(invoice.paidAmount ?? 0) + value,
                  dueAmount: Number(invoice.dueAmount ?? 0) - value,
                }
              : invoice,
          )
          // Settled in full: it is no longer an open invoice.
          .filter((invoice) => Number(invoice.dueAmount) > 0.004),
      );

      setPayTarget(null);
      load();

      /*
       * The payment box closes above. The supplier's invoice list sits behind it, and
       * used to be left open whatever happened - so after settling a supplier's last
       * invoice the screen still showed a dialog, now listing nothing, and it read as
       * though the payment had not gone through.
       *
       * So: still something owed, stay open on the refreshed list to pay the next one.
       * Nothing owed, close and go back to the dues list.
       */
      if (selected) {
        try {
          // false = do not hand back a cached list; this one has just changed.
          const fresh = await getOpenInvoices(selected.supplierId, false).unwrap();

          /*
           * Merged, not swapped in.
           *
           * This list is served through a cached query, and if it answers with the
           * figures from before the payment it would put the old Due straight back on
           * screen - undoing the line above and making the payment look as though it
           * had not registered. A due can only ever fall when money is paid, so where
           * the two disagree about the invoice just paid, the smaller figure is the
           * one that has caught up.
           */
          const left = fresh
            .map((invoice) =>
              // Still showing more than it should: that answer predates the payment.
              invoice.id === paidId && Number(invoice.dueAmount ?? 0) > expectedDue + 0.004
                ? { ...invoice, dueAmount: expectedDue, paidAmount: invoiceTotal - expectedDue }
                : invoice,
            )
            .filter((invoice) => Number(invoice.dueAmount) > 0.004);

          setInvoices(left);
          if (left.length === 0) {
            setSelected(null);
          } else {
            /*
             * The header above the list reads "x outstanding", and it comes from the
             * row the dues table was built from - which was fetched before the payment.
             * Refreshing only the invoices left the header showing the old total, so a
             * payment of 5,000 against 20,000 still said 20,000 and read as though
             * nothing had been recorded. Add up what is actually still open.
             */
            const stillOwed = left.reduce((sum, invoice) => sum + Number(invoice.dueAmount ?? 0), 0);
            setSelected((current) => (current ? { ...current, dueAmount: stillOwed, invoiceCount: left.length } : current));
          }
        } catch {
          setSelected(null);
        }
      }
    } catch (error: any) {
      // The backend refuses more than the invoice has outstanding.
      openSnackbar(error?.data?.message || 'Could not record the payment', 'error');
    }
  };

  return (
    <Box>
      <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center" sx={{ mb: 2 }}>
        <SearchField
          value={searchValue}
          onChange={setSearchValue}
          placeholder="Any words: supplier, phone, GSTIN, invoice no..."
          width={320}
        />
        <Chip
          label={onlyDue ? 'Only unpaid' : 'All suppliers'}
          color={onlyDue ? 'primary' : 'default'}
          variant={onlyDue ? 'filled' : 'outlined'}
          onClick={() => setOnlyDue((value) => !value)}
        />
        <Box flexGrow={1} />
        <Chip variant="outlined" label={`${rowCount} supplier(s) · ${totals.invoices} invoice(s)`} />
      </Stack>

      {/* The three numbers that matter, across the whole search - not the page. */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={4}>
          <Card sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              BILLED BY SUPPLIERS
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {money(totals.billed)}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              ALREADY PAID
            </Typography>
            <Typography variant="h6" fontWeight={700} color="success.main">
              {money(totals.paid)}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              STILL OWED IN TOTAL
            </Typography>
            <Typography variant="h5" fontWeight={700} color="error.main">
              {money(totals.due)}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      <DataGrid
        columns={columns}
        rows={rows}
        getRowId={(row) => row.supplierId}
        rowCount={rowCount}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={(model: GridPaginationModel) => setPaginationModel(model)}
        pageSizeOptions={[10, 25, 50, 100]}
        disableRowSelectionOnClick
        loading={loading}
        sx={{ height: '62vh' }}
      />

      {/* ----------------------------------------------- open invoices */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box>
            <Typography fontWeight={700} fontSize="1rem">
              {selected?.supplierName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ₹ {money(selected?.dueAmount)} outstanding
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setSelected(null)} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">Due</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    Every invoice is settled.
                  </TableCell>
                </TableRow>
              )}
              {invoices.map((invoice) => (
                <TableRow key={invoice.id} hover>
                  <TableCell>{invoice.invoiceNo}</TableCell>
                  <TableCell>{invoice.invoiceDate}</TableCell>
                  <TableCell align="right">{money(invoice.totalAmount)}</TableCell>
                  <TableCell align="right">{money(invoice.paidAmount)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {money(invoice.dueAmount)}
                  </TableCell>
                  <TableCell align="right">
                    {can('PURCHASE', 'create') && (
                      <Button size="small" variant="contained" onClick={() => openPayment(invoice)}>
                        Pay
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>

      {/* ---------------------------------------------------- pay one */}
      <Dialog open={!!payTarget} onClose={() => setPayTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography fontWeight={700} fontSize="1rem">
            Pay {payTarget?.invoiceNo}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ₹ {money(payTarget?.dueAmount)} outstanding
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField label="Amount" size="small" type="number" value={amount} onChange={(event) => setAmount(event.target.value)} />
            <TextField
              select
              label="Paid by"
              size="small"
              value={paymentMode}
              onChange={(event) => setPaymentMode(event.target.value as PurchasePayment['paymentMode'])}
            >
              <MenuItem value="CASH">Cash</MenuItem>
              <MenuItem value="UPI_CARD">UPI / Bank transfer</MenuItem>
            </TextField>
            {/* A payment cannot be made before the goods were invoiced, so the calendar
                will not offer an earlier day and the box says why if one is typed. */}
            <TextField
              label="Date"
              size="small"
              type="date"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: payTarget?.invoiceDate, max: today() }}
              error={!!payTarget?.invoiceDate && paymentDate < payTarget.invoiceDate}
              helperText={
                !!payTarget?.invoiceDate && paymentDate < payTarget.invoiceDate
                  ? `Not before the invoice date, ${payTarget.invoiceDate}`
                  : ' '
              }
              value={paymentDate}
              onChange={(event) => setPaymentDate(event.target.value)}
            />
            <TextField
              label="Reference"
              size="small"
              placeholder="Cheque or UPI reference"
              value={referenceNo}
              onChange={(event) => setReferenceNo(event.target.value)}
            />

            {history.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  ALREADY PAID
                </Typography>
                {history.map((payment) => (
                  <Stack key={payment.id} direction="row" justifyContent="space-between">
                    <Typography variant="caption">
                      {payment.paymentDate} · {payment.paymentMode}
                    </Typography>
                    <Typography variant="caption" fontWeight={600}>
                      {money(payment.amount)}
                    </Typography>
                  </Stack>
                ))}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" color="inherit" size="small" onClick={() => setPayTarget(null)}>
            Cancel
          </Button>
          <LoadingButton variant="contained" size="small" loading={paying} onClick={submitPayment}>
            Record Payment
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplierDues;
